<script lang="ts">
/**
 * @component ProjectCard
 *
 * A card component that showcases a single project in the portfolio's masonry
 * grid layout. Each card displays the project title, technology badges, a
 * descriptive snippet (rendered via Svelte `children` snippet), and a zoomable
 * project image powered by the {@link Lens} component.
 *
 * The card wraps everything in an `<LocalizedLink>` tag. Internal links are localized via
 * `intlayer`'s `getLocalizedUrl`, while external links open in a new tab with
 * `noopener noreferrer` for security.
 *
 * Uses the shadcn-svelte Card primitives (`Card.Root`, `Card.Header`, etc.)
 * with a `my-glass` glassmorphism class for the Nord-themed frosted appearance.
 *
 * Uses Liquid Glass material (my-glass) for translucent card surface. Placed in
 * CSS-columns masonry layout matching iPadOS UICollectionView flow pattern --
 * 1 column on iPhone, 2-3 on iPad, 3-4 on Mac.
 *
 * @see {@link project-card-skeleton.svelte} for the loading placeholder variant.
 *
 * @example
 * ```svelte
 * <ProjectCard
 *   projektImage="/images/my-app.png"
 *   projektImageAlt="Screenshot of My App"
 *   projektTitle="My App"
 *   projektBadges={['SvelteKit', 'TypeScript', 'Tailwind']}
 *   projektLink="/projects/my-app"
 * >
 *   A brief description of the project.
 * </ProjectCard>
 * ```
 */
import type { Snippet } from "svelte";
import * as Card from "$lib/components/ui/card";
import { Badge } from "$lib/components/ui/badge";
import { getLocalizedUrl, locale } from "$lib/i18n";
import { type LocaleCode } from "$lib/i18n/helpers";
import Lens from "$lib/components/Lens.svelte";
import { createWebHaptics } from "web-haptics/svelte";
import { onDestroy } from "svelte";
import LocalizedLink from "../LocalizedLink.svelte";
import { Footer } from "../ui/alert-dialog";
const { trigger, destroy } = createWebHaptics();
onDestroy(destroy);

/**
 * Props for the ProjectCard component.
 *
 * @property {string} projektImage - URL or path to the project screenshot/image.
 * @property {string} projektImageAlt - Alt text for the project image (accessibility).
 * @property {string} [projektImageBgColor=''] - Optional Tailwind background color class
 *   for the image container. Defaults to 'bg-background' if not provided.
 * @property {string} projektTitle - Display title of the project.
 * @property {string[]} projektBadges - Array of technology/tag labels rendered as Badge components.
 * @property {string} projektLink - URL to navigate to when the card is clicked. Internal
 *   paths are localized; external URLs (http/https) open in a new tab.
 * @property {Snippet} children - Svelte snippet for the project description content.
 */
interface Props {
  projektImage: string;
  projektImageAlt: string;
  projektImageBgColor?: string;
  projektTitle: string;
  projektBadges: string[];
  projektLink: string;
  children: Snippet;
}

let {
  projektImage,
  projektImageAlt,
  projektImageBgColor = "",
  projektTitle,
  projektBadges,
  projektLink,
  children,
}: Props = $props();

/** Whether `projektLink` points to an external URL (starts with http:// or https://). */
let isExternal = $derived(
  projektLink?.startsWith("http://") || projektLink?.startsWith("https://"),
);
/** The resolved href -- external links pass through unchanged; internal links are localized. */
let href = $derived(
  isExternal
    ? projektLink
    : getLocalizedUrl(projektLink, $locale as LocaleCode),
);
/** External links open in a new tab; internal links use default navigation. */
let target = $derived(isExternal ? "_blank" : undefined);
/** Security attributes for external links to prevent reverse tabnabbing. */
let rel = $derived(isExternal ? "noopener noreferrer" : undefined);
</script>

{#snippet imageSnippet(img: string, imgAlt: string, imgBgColor: string)}
  <div class="flex items-center justify-center p-4">
    <Lens zoomFactor={2} lensSize={150} ariaLabel="Zoom Area">
      <img
        src={img}
        alt={imgAlt}
        class="{imgBgColor ||
          'bg-background'} w-full h-full p-4 rounded-sm object-contain"
      />
    </Lens>
  </div>
{/snippet}

{#snippet contentSnippet(title: string, badges: string[], child: Snippet)}
  <div>
    <Card.Header>
      <Card.Title>{title}</Card.Title>
      <Card.Description>
        <span class="flex flex-wrap gap-2">
          {#each badges as badge}
            <Badge variant="default">{badge}</Badge>
          {/each}
        </span>
      </Card.Description>
    </Card.Header>
    <Card.Content>
      <section class="flex flex-col justify-between gap-4">
        <p>{@render child()}</p>
      </section>
    </Card.Content>
  </div>
{/snippet}

<LocalizedLink
  {href}
  {target}
  {rel}
  onclick={() => {
    trigger([
      { duration: 60, intensity: 1 },
      { delay: 30, duration: 60, intensity: 0.75 },
      { delay: 30, duration: 60 },
      { delay: 30, duration: 60, intensity: 0.75 },
      { delay: 30, duration: 60, intensity: 1 },
    ]);
  }}
  onpointerenter={() => {
    trigger([
      { duration: 60, intensity: 1 },
      { delay: 30, duration: 60, intensity: 0.75 },
      { delay: 30, duration: 60 },
      { delay: 30, duration: 60, intensity: 0.75 },
      { delay: 30, duration: 60, intensity: 1 },
    ]);
  }}
>
  <!-- Desktop (>768px):   Bild links,  Text rechts   -->
  <!-- Mobile  (<=768px):  Bild unten,  Text oben    -->
  <Card.Root
    class="my-glass hidden lg:flex flex-row overflow-hidden items-center justify-center"
  >
    <div class="w-1/2 justify-center">
      {@render imageSnippet(projektImage, projektImageAlt, projektImageBgColor)}
    </div>
    <div class="w-1/2 flex flex-col justify-center p-4">
      {@render contentSnippet(projektTitle, projektBadges, children)}
    </div>
  </Card.Root>

  <Card.Root
    class="my-glass lg:hidden flex flex-col overflow-hidden items-center justify-center"
  >
    <div class="flex flex-col p-4">
      {@render contentSnippet(projektTitle, projektBadges, children)}
    </div>
    <div class="w-full justify-center items-center">
      {@render imageSnippet(projektImage, projektImageAlt, projektImageBgColor)}
    </div>
  </Card.Root>
</LocalizedLink>
